import { ObligationRulesGeneratorConfirmationItem } from '@commenda/api';
import { ComplianceStatus } from '@commenda/prisma';
import type { PreviewRow } from './confirmationRows.types';

const ACTIONABLE_STATUSES: readonly ComplianceStatus[] = [
  ComplianceStatus.NOT_FILED,
  ComplianceStatus.UNKNOWN,
];

/**
 * Stable key for a filing's pending decision.
 */
export const idOfPendingDecision = (row: PreviewRow): string =>
  row.filingCompositeId ?? row.id;

/** A confirmation item still awaiting a decision on an actionable (not-yet-started) instance. */
export const isResolvable = (item: ObligationRulesGeneratorConfirmationItem) =>
  item.mode === 'GENERATE_AS_CONFIRMATION' &&
  !item.decision &&
  (item.status === null || ACTIONABLE_STATUSES.includes(item.status));

export const dedupeConfirmationRows = (
  items: ObligationRulesGeneratorConfirmationItem[]
): PreviewRow[] => {
  const groups = new Map<string, ObligationRulesGeneratorConfirmationItem[]>();
  items.forEach((item) => {
    // Items without a composite id (legacy/custom) can't be grouped — key them by their own id.
    const key = item.filingCompositeId ?? `__item__${item.id}`;
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  });

  return Array.from(groups.values()).map((group) => {
    const resolvable = group.find(isResolvable);
    const representative = resolvable ?? group[0];
    const confirmationMember = group.find(
      (i) => i.mode === 'GENERATE_AS_CONFIRMATION' && !i.decision
    );
    return {
      id: representative.id,
      filingCompositeId: representative.filingCompositeId,
      title: representative.title ?? 'Compliance item',
      mode: confirmationMember?.mode ?? representative.mode,
      decision: group.find((i) => i.decision)?.decision ?? null,
      canResolve: Boolean(resolvable),
      ruleDescription: representative.ruleDescription,
      category: representative.category,
      frequency: representative.frequency,
      dueDate: representative.dueDate,
      effectiveYear: representative.effectiveYear,
    };
  });
};

/** The un-decided needs-confirmation rows only. */
export const resolvableRows = (
  items: ObligationRulesGeneratorConfirmationItem[]
): PreviewRow[] =>
  dedupeConfirmationRows(items).filter((row) => row.canResolve);

export const countUnresolvedConfirmations = (
  items: ObligationRulesGeneratorConfirmationItem[]
): number =>
  new Set(
    items
      .filter(isResolvable)
      .map(
        (item) =>
          `${item.filingCompositeId ?? `__item__${item.id}`}::${item.effectiveYear ?? ''}`
      )
  ).size;

/** The latest fiscal year that still has an unresolved needs-confirmation item, or null if none. */
export const latestUnresolvedFiscalYear = (
  items: ObligationRulesGeneratorConfirmationItem[]
): number | null => {
  const years = items
    .filter(isResolvable)
    .map((item) => item.effectiveYear)
    .filter((year): year is number => year != null);
  return years.length > 0 ? Math.max(...years) : null;
};
